/**
 * 通用队列工具 - 数据库操作
 * 基于 IndexedDB (Dexie.js) 实现的客户端队列存储
 */

import Dexie, { Table } from 'dexie'
import { QueueItem } from './types'

/**
 * 评分队列数据结构（通用）
 * 业务代码可以扩展此接口
 */
interface BaseScoreQueueData {
  recordIds: number[]
  [key: string]: unknown
}

/** 默认过期时间：7天 */
const DEFAULT_MAX_AGE = 7 * 24 * 60 * 60 * 1000

/**
 * 队列数据库类
 */
class QueueDatabase extends Dexie {
  queues!: Table<QueueItem>

  constructor () {
    super('AppQueueDB')

    this.version(1).stores({
      // 索引设计：
      // ++id       : 自增主键
      // queueId    : 用于精确查询单个队列
      // category   : 用于按类型查询队列列表
      // createdAt  : 用于过期数据清理
      queues: '++id, queueId, category, createdAt',
    })
  }
}

/** 数据库单例实例 */
const db = new QueueDatabase()

/**
 * 存储队列
 * @param category - 队列分类
 * @param data - 队列数据
 * @returns 队列ID（时间戳）
 */
export async function storeQueue<T> (category: string, data: T): Promise<number> {
  const now = Date.now()

  // 存储前先清理过期数据
  await cleanExpiredQueues()

  await db.queues.add({
    queueId: now,
    category,
    data,
    createdAt: now,
  })

  return now
}

/**
 * 获取队列
 * @param queueId - 队列ID
 * @returns 队列项（包含完整信息）
 */
export async function getQueue<T> (queueId: number): Promise<QueueItem<T> | undefined> {
  const item = await db.queues.where('queueId').equals(queueId).first()
  return item as QueueItem<T> | undefined
}

/**
 * 获取队列数据
 * @param queueId - 队列ID
 * @returns 队列数据（只返回 data 字段）
 */
export async function getQueueData<T> (queueId: number): Promise<T | undefined> {
  const item = await getQueue<T>(queueId)
  return item?.data
}

/**
 * 更新队列数据
 * @param queueId - 队列ID
 * @param data - 要更新的数据（会与原数据合并）
 */
export async function updateQueue<T> (queueId: number, data: Partial<T>): Promise<void> {
  const existing = await getQueue<T>(queueId)
  if (existing && existing.id !== undefined) {
    await db.queues.update(existing.id, {
      data: { ...existing.data, ...data },
    })
  }
}

/**
 * 替换队列数据
 * @param queueId - 队列ID
 * @param data - 新的完整数据（会完全替换原数据）
 */
export async function replaceQueueData<T> (queueId: number, data: T): Promise<void> {
  const existing = await getQueue<T>(queueId)
  if (existing && existing.id !== undefined) {
    await db.queues.update(existing.id, { data })
  }
}

/**
 * 删除队列
 * @param queueId - 队列ID
 */
export async function deleteQueue (queueId: number): Promise<void> {
  await db.queues.where('queueId').equals(queueId).delete()
}

/**
 * 按分类获取队列列表
 * @param category - 队列分类
 * @returns 队列列表
 */
export async function getQueuesByCategory<T> (category: string): Promise<QueueItem<T>[]> {
  const items = await db.queues.where('category').equals(category).toArray()
  return items as QueueItem<T>[]
}

/**
 * 清理过期队列
 * @param maxAge - 最大存活时间（毫秒），默认7天
 * @returns 清理的记录数
 */
export async function cleanExpiredQueues (maxAge: number = DEFAULT_MAX_AGE): Promise<number> {
  const expireTime = Date.now() - maxAge
  return db.queues.where('createdAt').below(expireTime).delete()
}

/**
 * 获取数据库实例（用于 dexie-react-hooks）
 */
export function getQueueDB () {
  return db
}

// =============================================================================
// 评分队列专用辅助方法
// =============================================================================

/**
 * 获取下一条记录ID
 * @param queueId - 队列ID
 * @param currentRecordId - 当前记录ID
 * @returns 下一条记录ID，如果没有则返回 undefined
 */
export async function getNextRecordId (
  queueId: number,
  currentRecordId: number
): Promise<number | undefined> {
  const data = await getQueueData<BaseScoreQueueData>(queueId)
  if (!data) return undefined

  const { recordIds } = data
  const currentIndex = recordIds.findIndex((id: number) => id === currentRecordId)
  if (currentIndex === -1) return undefined

  const nextIndex = currentIndex + 1
  return recordIds[nextIndex]
}

/**
 * 获取上一条记录ID
 * @param queueId - 队列ID
 * @param currentRecordId - 当前记录ID
 * @returns 上一条记录ID，如果没有则返回 undefined
 */
export async function getPrevRecordId (
  queueId: number,
  currentRecordId: number
): Promise<number | undefined> {
  const data = await getQueueData<BaseScoreQueueData>(queueId)
  if (!data) return undefined

  const { recordIds } = data
  const currentIndex = recordIds.findIndex((id: number) => id === currentRecordId)
  if (currentIndex <= 0) return undefined

  return recordIds[currentIndex - 1]
}

/**
 * 获取当前记录在队列中的位置信息
 * @param queueId - 队列ID
 * @param currentRecordId - 当前记录ID
 * @returns 位置信息
 */
export async function getRecordPosition (
  queueId: number,
  currentRecordId: number
): Promise<{ current: number; total: number; hasPrev: boolean; hasNext: boolean } | undefined> {
  const data = await getQueueData<BaseScoreQueueData>(queueId)
  if (!data) return undefined

  const { recordIds } = data
  const currentIndex = recordIds.findIndex((id: number) => id === currentRecordId)
  if (currentIndex === -1) return undefined

  return {
    current: currentIndex + 1,
    total: recordIds.length,
    hasPrev: currentIndex > 0,
    hasNext: currentIndex < recordIds.length - 1,
  }
}

/**
 * 追加更多记录到队列（用于分页加载）
 * @param queueId - 队列ID
 * @param newRecordIds - 新的记录ID列表
 */
export async function appendRecordIds (
  queueId: number,
  newRecordIds: number[]
): Promise<void> {
  const data = await getQueueData<BaseScoreQueueData>(queueId)
  if (!data) return

  await updateQueue<BaseScoreQueueData>(queueId, {
    recordIds: [...data.recordIds, ...newRecordIds],
  })
}
